import axios, { endpoints } from 'src/lib/axios';

import { setSession } from './utils';

// ----------------------------------------------------------------------

export type SignInParams = {
  email: string;
  password: string;
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({ email, password }: SignInParams): Promise<void> => {
  try {
    const params = { email, password };

    const res = await axios.post(endpoints.auth.signIn, params);

    const { accessToken, refreshToken } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    setSession(accessToken, refreshToken);
  } catch (error) {
    console.error('Error during sign in:', error);
    throw error;
  }
};

/** **************************************
 * Forgot password
 *
 * Fire-and-forget: the backend always answers the same generic message whether
 * or not the email is an admin (no account enumeration), so there is nothing to
 * read from the response — a resolved promise means "request accepted".
 *************************************** */
export type ForgotPasswordParams = {
  email: string;
};

export const requestPasswordReset = async ({ email }: ForgotPasswordParams): Promise<void> => {
  try {
    await axios.post(endpoints.auth.forgotPassword, { email });
  } catch (error) {
    console.error('Error during forgot-password request:', error);
    throw error;
  }
};

/** **************************************
 * Reset password
 *
 * Consumes the single-use token from the emailed link and sets the new
 * password. Does NOT sign the user in — they land back on the sign-in page.
 *************************************** */
export type ResetPasswordParams = {
  token: string;
  password: string;
};

export const resetPassword = async ({ token, password }: ResetPasswordParams): Promise<void> => {
  try {
    await axios.post(endpoints.auth.resetPassword, { token, password });
  } catch (error) {
    console.error('Error during password reset:', error);
    throw error;
  }
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async (): Promise<void> => {
  try {
    await setSession(null);
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};
